import { useMemo } from 'react';
import {
    calculateBalancesForSettlements,
    calculateBalancesWithSponsorReimbursement,
    getSettlementsByStrategy,
} from '../utils/helpers';
import {
    Balance,
    Expense,
    Settlement,
    SettlementConfig,
    SettlementStatusItem,
    SettlementStrategy,
    ShareRatios,
    SponsorExpense,
} from '../types';

/**
 * How a transfer is addressed in `events/<id>/settlementStatus`.
 *
 * The key is the pair itself, which is why changing the plan drops every mark:
 * another strategy produces different pairs and would orphan them.
 */
export const settlementKeyOf = (from: string, to: string) => `${from}_to_${to}`;

export interface SettlementPlan {
    strategy: SettlementStrategy;
    collectorId: string;
    distributeSponsorSurplus: boolean;
    balances: Balance;
    /** Who owes whom, undecorated — names and sponsor flags are a view's business. */
    settlements: Settlement[];
    /** Transfers in this plan that someone has marked as made. */
    paidCount: number;
    /** True once any of them has, which freezes the plan. */
    isPlanLocked: boolean;
}

/**
 * The settlement plan for an event: who pays whom, and how far along it is.
 *
 * It lives in a hook because two screens read it and only one of them draws it.
 * The payments tab needs every part; the expense summary needs one number off it
 * ("3 of 5 transfers made"), and a second derivation of the same plan would be a
 * second thing to keep in step — the surest way for that number to end up quietly
 * disagreeing with the tab it summarises.
 */
export const useSettlementPlan = (
    expenses: (Expense | SponsorExpense)[],
    participants: Record<string, string>,
    settlementConfig?: SettlementConfig,
    settlementStatus: Record<string, SettlementStatusItem> = {},
    shareRatios?: ShareRatios,
): SettlementPlan => {
    const participantIds = useMemo(() => Object.keys(participants), [participants]);

    // The plan is shared by the whole event, so it lives in Firebase. These are the
    // defaults for an event where nobody has picked anything yet.
    const strategy = settlementConfig?.strategy ?? 'simplePay';
    const collectorId = settlementConfig?.collectorId || participantIds[0] || '';
    const distributeSponsorSurplus = settlementConfig?.distributeSponsorSurplus ?? true;

    const balances = useMemo(() => (
        distributeSponsorSurplus
            ? calculateBalancesForSettlements(expenses, participantIds, shareRatios)
            : calculateBalancesWithSponsorReimbursement(expenses, participantIds, shareRatios)
    ), [distributeSponsorSurplus, expenses, participantIds, shareRatios]);

    const settlements = useMemo(
        () => getSettlementsByStrategy(balances, strategy, collectorId),
        [balances, strategy, collectorId]
    );

    // Counted against the current plan rather than by tallying the whole status
    // node: a mark left over from a plan that has since changed is not a transfer
    // anybody is still waiting on.
    const paidCount = useMemo(
        () => settlements.filter(s => settlementStatus[settlementKeyOf(s.from, s.to)]?.paid).length,
        [settlements, settlementStatus]
    );

    const isPlanLocked = useMemo(
        () => Object.values(settlementStatus).some(s => s?.paid),
        [settlementStatus]
    );

    return {
        strategy,
        collectorId,
        distributeSponsorSurplus,
        balances,
        settlements,
        paidCount,
        isPlanLocked,
    };
};
